import React, { createContext, useContext, useState, useEffect, useRef, useCallback, ReactNode } from 'react';
import NoSleep from '@zakj/no-sleep';

interface GPSContextType {
  isFetching: boolean;
  coordinates: GeolocationCoordinates | null;
  maxSpeed: number;
  totalDistance: number;
  startTime: number | null;
  isGeolocationAvailable: boolean;
  isGeolocationEnabled: boolean;
  geoError: string | null;
  startTracking: () => void;
  stopTracking: () => void;
  resetMaxSpeed: () => void;
}

const GPSContext = createContext<GPSContextType | undefined>(undefined);

export const useGPS = () => {
  const context = useContext(GPSContext);
  if (!context) {
    throw new Error('useGPS must be used within a GPSProvider');
  }
  return context;
};

interface GPSProviderProps {
  children: ReactNode;
}

export const GPSProvider: React.FC<GPSProviderProps> = ({ children }) => {
  const [isFetching, setIsFetching] = useState(false);
  const [maxSpeed, setMaxSpeed] = useState<number>(() => {
    const storedMaxSpeed = localStorage.getItem("maxSpeed");
    return storedMaxSpeed ? parseInt(storedMaxSpeed) : 0;
  });

  const [totalDistance, setTotalDistance] = useState<number>(0);
  const [startTime, setStartTime] = useState<number | null>(null);
  const [coordinates, setCoordinates] = useState<GeolocationCoordinates | null>(null);
  const [isGeolocationAvailable, setIsGeolocationAvailable] = useState(true);
  const [isGeolocationEnabled, setIsGeolocationEnabled] = useState(false);
  const [geoError, setGeoError] = useState<string | null>(null);

  const watchIdRef = useRef<number | null>(null);
  const noSleepRef = useRef<any>(null);
  const prevCoordinatesRef = useRef<GeolocationCoordinates | null>(null);

  // Initialize NoSleep once
  useEffect(() => {
    noSleepRef.current = new NoSleep();
    
    // Register native location callback for background updates
    (window as any).nativeLocationUpdate = (location: any) => {
      console.log('📱 Native location received:', location);
      
      const coords: GeolocationCoordinates = {
        latitude: location.latitude,
        longitude: location.longitude,
        accuracy: location.accuracy,
        altitude: null,
        altitudeAccuracy: null,
        heading: null,
        speed: location.speed >= 0 ? location.speed : null
      };
      
      setCoordinates(coords);
    };
    
    // Register native session restore callback
    (window as any).nativeSessionRestore = (session: any) => {
      console.log('🔄 Native session restore:', session);
      
      if (session.trackingActive) {
        setTotalDistance(session.totalDistance || 0);
        setMaxSpeed(session.maxSpeed || 0);
        setStartTime(session.startTime || Date.now());
        setIsFetching(true);
        console.log(`✅ Restored: Distance=${session.totalDistance}km, Max=${session.maxSpeed}km/h`);
      }
    };
    
    // Check for native session data on startup
    const checkNativeSession = () => {
      try {
        const stored = localStorage.getItem('trackingSession');
        if (stored) {
          const session = JSON.parse(stored);
          if (session.syncedFromNative && session.trackingActive) {
            console.log('🔄 Found native session in localStorage:', session);
            setTotalDistance(session.totalDistance || 0);
            setMaxSpeed(session.maxSpeed || 0);
            setStartTime(session.startTime || Date.now());
            setIsFetching(true);
          }
        }
      } catch (e) {
        console.warn('Failed to restore session:', e);
      }
    };
    
    checkNativeSession();
    
    return () => {
      if (noSleepRef.current) {
        noSleepRef.current.disable();
      }
      delete (window as any).nativeLocationUpdate;
      delete (window as any).nativeSessionRestore;
    };
  }, []);

  // Check if geolocation is available
  useEffect(() => {
    if (!("geolocation" in navigator)) {
      setIsGeolocationAvailable(false);
      setGeoError("Geolocation is not supported by your browser");
    }
  }, []);

  // Handle geolocation position updates
  const handlePosition = useCallback((position: GeolocationPosition) => {
    setIsGeolocationEnabled(true);
    setGeoError(null);
    
    const currentSpeed = Math.floor((position.coords.speed || 0) * 3.6);
    
    // Update max speed
    const storedMaxSpeed = localStorage.getItem("maxSpeed");
    const maxSpeedLocal = parseInt(storedMaxSpeed || "0");
    
    if (currentSpeed > maxSpeedLocal) {
      setMaxSpeed(currentSpeed);
      localStorage.setItem("maxSpeed", currentSpeed.toString());
    }

    setCoordinates(position.coords);
  }, []);

  // Handle geolocation errors
  const handleError = useCallback((error: GeolocationPositionError) => {
    let errorMessage = "An unknown error occurred";
    
    switch (error.code) {
      case error.PERMISSION_DENIED:
        errorMessage = "Location permission denied. Please enable location access.";
        setIsGeolocationEnabled(false);
        break;
      case error.POSITION_UNAVAILABLE:
        errorMessage = "Location information unavailable. Please check GPS signal.";
        break;
      case error.TIMEOUT:
        errorMessage = "Location request timed out. Retrying...";
        break;
    }
    
    setGeoError(errorMessage);
    console.error("Geolocation error:", errorMessage, error);
  }, []);

  // Calculate distance between two coordinates using Haversine formula
  const calculateDistance = useCallback((fromPos: GeolocationCoordinates | null, toPos: GeolocationCoordinates): number => {
    if (!fromPos) {
      return 0;
    }

    const radius = 6371; // Earth's radius in kilometers
    const toRad = (number: number) => number * Math.PI / 180;

    const latDistance = toRad(toPos.latitude - fromPos.latitude);
    const lonDistance = toRad(toPos.longitude - fromPos.longitude);
    
    const a = Math.sin(latDistance / 2) * Math.sin(latDistance / 2) + 
              Math.cos(toRad(fromPos.latitude)) * Math.cos(toRad(toPos.latitude)) * 
              Math.sin(lonDistance / 2) * Math.sin(lonDistance / 2);

    return radius * (2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a))); 
  }, []);

  // Update total distance when coordinates change
  useEffect(() => {
    if (isFetching && coordinates && coordinates.latitude && coordinates.longitude) {
      if (prevCoordinatesRef.current) {
        const distance = calculateDistance(prevCoordinatesRef.current, coordinates);
        // Only update if distance is significant (> 1 meter) and reasonable (< 100 meters between updates)
        if (distance > 0.001 && distance < 0.1) {
          setTotalDistance(prev => prev + distance);
        }
      }
      prevCoordinatesRef.current = coordinates;
    }
  }, [coordinates, isFetching, calculateDistance]);

  // Start/stop GPS watching
  useEffect(() => {
    if (isFetching && isGeolocationAvailable) {
      const options: PositionOptions = {
        enableHighAccuracy: true,
        timeout: 10000,
        maximumAge: 0
      };

      watchIdRef.current = navigator.geolocation.watchPosition(
        handlePosition,
        handleError,
        options
      );

      return () => {
        if (watchIdRef.current !== null) {
          navigator.geolocation.clearWatch(watchIdRef.current);
          watchIdRef.current = null;
        }
      };
    }
  }, [isFetching, isGeolocationAvailable, handlePosition, handleError]);

  const startTracking = useCallback(() => {
    if (!isFetching && isGeolocationAvailable) {
      // Start native GPS tracking on iOS for background support
      if ((window as any).webkit?.messageHandlers?.startTracking) {
        try {
          (window as any).webkit.messageHandlers.startTracking.postMessage({});
          console.log('🚀 Native GPS tracking started');
        } catch (err) {
          console.warn('Native tracking start failed:', err);
        }
      }
      
      // Enable NoSleep based on user preference
      const keepScreenOn = localStorage.getItem("keepScreenOn");
      const shouldKeepScreenOn = keepScreenOn === null || keepScreenOn === "true";
      
      if (shouldKeepScreenOn && noSleepRef.current) {
        const enableNoSleep = () => {
          document.removeEventListener('click', enableNoSleep);
          noSleepRef.current?.enable();
        };
        document.addEventListener('click', enableNoSleep, { once: true });
      }

      // Reset tracking data
      setTotalDistance(0);
      setStartTime(Date.now());
      prevCoordinatesRef.current = null;
      setIsFetching(true);
    }
  }, [isFetching, isGeolocationAvailable]);

  const stopTracking = useCallback(() => {
    setIsFetching(false);
    if (noSleepRef.current) {
      noSleepRef.current.disable();
    }
    
    // Stop native GPS tracking on iOS
    if ((window as any).webkit?.messageHandlers?.stopTracking) {
      try {
        (window as any).webkit.messageHandlers.stopTracking.postMessage({});
        console.log('🛑 Native GPS tracking stopped');
      } catch (err) {
        console.warn('Native tracking stop failed:', err);
      }
    }
  }, []);

  const resetMaxSpeed = useCallback(() => {
    setMaxSpeed(0);
    localStorage.setItem("maxSpeed", "0");
  }, []);

  const value: GPSContextType = {
    isFetching,
    coordinates,
    maxSpeed,
    totalDistance,
    startTime,
    isGeolocationAvailable,
    isGeolocationEnabled,
    geoError,
    startTracking,
    stopTracking,
    resetMaxSpeed,
  };

  return <GPSContext.Provider value={value}>{children}</GPSContext.Provider>;
};
